import React, { useEffect, useState } from "react";
import { useMineCards } from "../../../utils/reactQuery/cards";
import { useSelector, useDispatch } from "react-redux";
import {
  useGenericWorkflowStatus,
  useUserWorkflowList,
} from "../../../utils/reactQuery/workflows";
import Loader from "../../../components/Loader";
import KanbanCard from "../../../components/Kanban/KanbanCard";
import { Tooltip } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PinIcon from "../../../styles/svg/pin.svg";
import { usePlannerTodos } from "../../../utils/reactQuery/plannerTodos";
import { useWatching } from "../../../utils/reactQuery/users";
import { combineAndFilterUnique } from "../../../utils/arrayUtils";
import OpenCardDetail from "../../board/components/Kanban/openCardDetail";
import { useCustomSearchParams } from "../../../hooks/useCustomSearchParams";
import { setTaskCount, setFilteredTasks } from "../../../redux/plannerFilterSlice";
import { Droppable } from "react-beautiful-dnd";

// Custom style for dragging
const dragStyles = {
  width: "100%",
  maxWidth: "100%",
};

/**
 * Get unique filtered cards based on workflow status
 * @param {Object} watchingData - Data from watching API
 * @param {Object} mineCards - Data from mineCards API
 * @param {Object} genericWorkflowStatus - Generic workflow status data
 * @returns {Object} - Object containing filtered cards and unique combined cards
 */
const getUniqueFilteredCards = (
  watchingData,
  mineCards,
  genericWorkflowStatus
) => {
  // Find the "Open" and "Triage" status IDs
  const openStatuses =
    genericWorkflowStatus?.data?.data?.responseData?.filter((status) =>
      ["open", "triage"].includes(status.status.toLowerCase())
    ) || [];

  const statusIds = openStatuses.map((status) => status.workflow_step_id);

  if (statusIds.length === 0) {
    console.warn("No Open or Triage workflow statuses found");
    return { watchingCards: [], assignedCards: [], combinedCards: [] };
  }

  // Filter watching cards by open status
  const watchingCards =
    watchingData?.data?.data?.responseData?.filter(
      (card) =>
        card?.generic_step_id && statusIds.includes(card.generic_step_id)
    ) || [];

  // Filter assigned cards by open/triage status or todo column
  const assignedCards =
    mineCards?.data?.data?.responseData?.filter(
      (card) =>
        (card?.generic_step_id && statusIds.includes(card.generic_step_id)) ||
        card?.card?.todo_column === "Open"
    ) || [];

  // Combine and filter unique cards
  const combinedCards = combineAndFilterUnique(
    [watchingCards, assignedCards],
    ["card", "card_id"]
  );

  return {
    watchingCards,
    assignedCards,
    combinedCards,
  };
};

function CardLists({ filter }) {
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [openDetailCard, setOpenDetailCard] = useState(false);
  const [cardDetailId] = useCustomSearchParams("card_id");
  const dispatch = useDispatch();

  const user = useSelector((state) => state.search.myTaskUser);
  const { isLoading: genericWorkflowStepsLoading, genericWorkflowStatus } =
    useGenericWorkflowStatus();
  const { userWorkflows } = useUserWorkflowList();
  const genericWorkflowId = userWorkflows?.data?.data?.responseData?.find(
    (workflow) => workflow.is_generic
  );

  const {
    todos,
    isLoading: isLoadingTodos,
    error,
    isError,
  } = usePlannerTodos();
  const {
    data: mineCards,
    isLoading,
    refetch,
    isRefetching,
  } = useMineCards(user || [], [], [], [], genericWorkflowId?.workflow_id);
  const { data: watchingData } = useWatching(genericWorkflowId?.workflow_id);

  // Set initial cards when data is loaded
  useEffect(() => {
    if (watchingData && mineCards && genericWorkflowStatus) {
      const { combinedCards } = getUniqueFilteredCards(
        watchingData,
        mineCards,
        genericWorkflowStatus
      );
      setCards(combinedCards);
    }
  }, [
    watchingData?.data?.data?.responseData,
    mineCards?.data?.data?.responseData,
    genericWorkflowStatus?.data?.data?.responseData,
  ]);

  useEffect(() => {
    refetch();
  }, [genericWorkflowId?.workflow_id]);

  // Filter cards based on selected filter
  useEffect(() => {
    if (watchingData && mineCards && genericWorkflowStatus) {
      const { watchingCards, assignedCards, combinedCards } =
        getUniqueFilteredCards(watchingData, mineCards, genericWorkflowStatus);

      let filteredResult = [];
      if (filter === "All") {
        filteredResult = combinedCards;
      } else if (filter === "Mine") {
        filteredResult = assignedCards?.filter((card) => !card?.card?.is_todo);
      } else if (filter === "Quick Tasks") {
        filteredResult = assignedCards?.filter((card) => card?.card?.is_todo);
      } else if (filter === "Watching") {
        filteredResult = watchingCards;
      }

      setFilteredCards(filteredResult);
      
      // Dispatch filteredCards to Redux
      dispatch(setFilteredTasks(filteredResult));
    }
  }, [filter, watchingData, mineCards, genericWorkflowStatus, dispatch]);


  // Check if a card is pinned in todos
  const isCardPinned = (cardId) => {
    if (!todos?.data) return false;
    return todos.data.some(
      (todo) =>
        todo.source_id && todo.source_id.toString() === cardId.toString()
    );
  };

  useEffect(() => {
    if (!cardDetailId) {
      setOpenDetailCard(false);
    }
  }, [cardDetailId]);

  useEffect(() => {
    if (cardDetailId) {
      const cardExists = mineCards?.data?.data?.responseData?.some(
        (card) => card.card?.card_id == cardDetailId
      );
      if (cardExists && !isCardPinned(cardDetailId)) {
        setOpenDetailCard(true);
      }
    }
  }, [cardDetailId, mineCards, todos]);

  useEffect(() => {
    if (filteredCards.length >= 0) {
      dispatch(setTaskCount(filteredCards.length || 0));
    }
  }, [filteredCards]);

  return (
    <>
      {openDetailCard && <OpenCardDetail cardsOnBoard={filteredCards} />}
      {isLoading && (
        <div className="loader-center">
          <Loader color="black" />
        </div>
      )}
      {!isLoading &&
        filteredCards?.map((card) => {
          // Skip rendering if card or card.card is null/undefined
          if (!card || !card.card || !card.card.card_id) {
            console.warn("Invalid card data found:", card);
            return null;
          }

          const isPinned = isCardPinned(card.card.card_id);
          return (
            // <div
            //   className={`draggable-card-container  ${
            //     isPinned ? "greyed-out" : ""
            //   }`}
            //   key={card.card.card_id}
            //   style={dragStyles}
            //   onDragStart={(e) => handleDragStart(e, card)}
            //   // onDragEnd={handleDragEnd}
            //   // draggable={true}
            // >
            <Droppable droppableId="right-tasks">
              {(provided) => (
                <div
                  // className="task-list"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`draggable-card-container  ${
                    isPinned ? "greyed-out" : ""
                  }`}
                  key={card.card.card_id}
                  style={dragStyles}
                >
                  {isPinned && (
                    <div className="pin-icon-container">
                      <img src={PinIcon} alt="pinned" className="pin-icon" />
                    </div>
                  )}
                  <div className="todo-card-status">
                      <p>{card.workflow_step_status}</p>
                  </div>
 
                  <KanbanCard
                    project={card}
                    onDragEnd={() => {}}
                    boardName={card.board_name || ""}
                    index={card.index || 0}
                    id={card.card.card_id}
                    status={card.status || ""}
                    isTodo={Boolean(card.card.is_todo)}
                    isAssignedUser={Boolean(card.is_assigned_user)}
                    isMyTaskCard={false}
                    boardKey={card.board_key || ""}
                    isMyPlanner={true}
                    style={dragStyles}
                    openDetailCard={() => {
                      setOpenDetailCard(true);
                    }}
                  />
                </div>
              )}
            </Droppable>
            // </div>
          );
        })}
    </>
  );
}

export default CardLists;
